import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import useRefCallback from '@hzdg/use-ref-callback';
import useScrolling from './useScrolling';
import useScrollPosition from './useScrollPosition';
import useScrollDirection from './useScrollDirection';
import useScrollIntersection from './useScrollIntersection';
/**
 * Scroll state passed to the ScrollMonitor render prop (children function).
 */

/**
 * A React component for monitoring and interacting with scroll position.
 *
 * `ScrollMonitor` is a headless component, meaning
 * it accepts a child render prop, and returns the result
 * of calling that render prop with the latest state of
 * the nearest scrollable container.
 */
function ScrollMonitor(props) {
  var innerRef = props.innerRef;
  var _useRefCallback = useRefCallback(null, innerRef),
    _useRefCallback2 = _slicedToArray(_useRefCallback, 2),
    scrollRef = _useRefCallback2[0],
    setScrollRef = _useRefCallback2[1];
  var scrolling = useScrolling(scrollRef);
  var direction = useScrollDirection(scrollRef);
  var intersects = useScrollIntersection(scrollRef, props.intersects || []);
  var _useState = useState({
      top: null,
      left: null
    }),
    _useState2 = _slicedToArray(_useState, 2),
    position = _useState2[0],
    setPosition = _useState2[1];
  useScrollPosition(scrollRef, setPosition);
  var scrollingEnabled = props.scrolling || Boolean(props.onStart || props.onChange || props.onEnd);
  var positionEnabled = props.position || Boolean(props.onChange);
  var directionEnabled = Boolean(props.direction);
  var intersectionEnabled = Boolean(props.intersects); // Create the new state.

  var state = useMemo(function () {
    var state = {
      scrollRef: setScrollRef
    };
    if (scrollingEnabled) state.scrolling = scrolling;
    if (positionEnabled) state.position = position;
    if (directionEnabled) state.direction = direction;
    if (intersectionEnabled) state.intersects = intersects;
    return state;
  }, [setScrollRef, scrolling, position, direction, intersects, scrollingEnabled, positionEnabled, directionEnabled, intersectionEnabled]); // Call the `onStart` hook if the scroll container is scrolling.

  var onStart = props.onStart;
  useMemo(function () {
    if (typeof onStart === 'function' && scrolling) {
      onStart();
    }
  }, [onStart, scrolling]); // Call the `onChange` hook if the scroll container is scrolling.

  var onChange = props.onChange;
  useMemo(function () {
    if (typeof onChange === 'function' && scrolling) {
      onChange(state);
    }
  }, [onChange, scrolling, state]); // Call the `onEnd` hook if the scroll container is not scrolling.

  var onEnd = props.onEnd; // Keep track whether or not the scroll container
  // was scrolling during the previous `onEnd` effect.

  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    wasScrolling = _useState4[0],
    setWasScrolling = _useState4[1];
  useMemo(function () {
    // Call the `onEnd` hook if scrolling just stopped.
    if (typeof onEnd === 'function' && wasScrolling && !scrolling) {
      onEnd();
    } // Set whether or not the scroll container was scrolling during this effect.

    setWasScrolling(scrolling);
  }, [onEnd, wasScrolling, scrolling]); // Call the render prop with the current state.

  var render = props.children;
  return render(state);
}
ScrollMonitor.propTypes = {
  children: PropTypes.func.isRequired,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.node
  })]),
  vertical: PropTypes.bool,
  horizontal: PropTypes.bool,
  direction: PropTypes.bool,
  position: PropTypes.bool,
  scrolling: PropTypes.bool,
  intersects: PropTypes.oneOfType([PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number
  }), PropTypes.arrayOf(PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number
  }))]),
  onStart: PropTypes.func,
  onChange: PropTypes.func,
  onEnd: PropTypes.func
};
export default ScrollMonitor;